<template>
	<div class="gastos_tab report_theme_container" data-theme="gastos">
		<!-- ================================================================ -->
		<!-- Header Section                                                    -->
		<!-- ================================================================ -->
		<div class="header_section_header mb-5">
			<div class="header_left">
				<div class="header_title">Gastos</div>
				<div class="header_description mb-4">
					Analiza la tendencia y composición de tus gastos
				</div>

				<!-- Sub-Navigation Filters -->
				<div class="sub_nav_filters">
					<button v-for="period in filterOptions" :key="period.id" class="filter_btn"
						:class="{ 'is-active': filterPeriod === period.id }" @click="updateFilterPeriod(period.id)">
						{{ period.label }}
					</button>
				</div>
			</div>

			<!-- Center: filtros de visualización (Cuentas + tipos de contraparte) -->
			<div class="center_sub_nav">
				<div class="filters_row is-flex is-align-items-center">
					<div class="sub_nav_filters">
						<button type="button"
							class="filter_btn"
							:class="{ 'is-active': isCuentasMode }"
							@click.stop="setCuentasMode">
							Cuentas
						</button>
					</div>

					<MultiSelectDropdown
						:value="selectedCounterpartTypes"
						:options="counterpartTypeOptions"
						placeholder="Tipos de contraparte"
						@input="SET_SELECTED_COUNTERPART_TYPES" />
				</div>
			</div>

			<div class="right_actions is-flex">
				<div class="calendar_picker is-flex is-align-items-center mr-3">
					<AnualCalendar class="calendar" :value="endMonth" @onDateChange="updateEndMonth"
						:disableNewerDates="true" :disabledFrom="getNow" formatLabelDate="MMMM YYYY"
						:alignRight="true" />
				</div>
				<button class="theme_btn_white is-flex is-align-items-center">
					<DownloadFile class="mr-2" style="width: 16px; height: 16px;" />
					Exportar
				</button>
			</div>
		</div>

		<!-- Summary Cards Section -->
		<ExpensesSummaryCards class="mt-5"
			:monthHeaders="monthHeaders"
			:tableRows="tableRows"
			:totalRow="totalRow"
			:isCuentasMode="isCuentasMode" />

		<!-- Main Table Section -->
		<ExpensesEvolutionTable class="mt-6"
			:monthHeaders="monthHeaders"
			:visibleRows="visibleRows"
			:totalRow="totalRow"
			:ratioRow="ratioRow"
			:expandedL2Rows="expandedL2Rows"
			:isLoading="isLoading"
			:error="error"
			:selectedPeriod="selectedPeriodRange"
			:periodOptions="periodOptions"
			@toggle-l2="toggleL2"
			@change-period="setPeriodRange"
			@retry="fetchExpensesData" />
	</div>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import AnualCalendar from "@/components/classification/AnualCalendar";
import DownloadFile from "@/components/svg/DownloadFile.vue";
import MultiSelectDropdown from "@/components/generics/MultiSelectDropdown.vue";
import ExpensesSummaryCards from "@/components/financialReports/ExpensesSummaryCards.vue";
import ExpensesEvolutionTable from "@/components/financialReports/ExpensesEvolutionTable.vue";
import {
	PERIOD_TYPES,
	EXPENSE_COUNTERPART_TYPES,
	FILTER_PERIOD_OPTIONS,
} from "../config/reportStructure";

export default {
	name: "GastosTab",

	components: {
		AnualCalendar,
		DownloadFile,
		MultiSelectDropdown,
		ExpensesSummaryCards,
		ExpensesEvolutionTable,
	},

	data() {
		return {
			periodOptions: PERIOD_TYPES,
			counterpartTypeOptions: EXPENSE_COUNTERPART_TYPES,
			expandedL2Rows: [],
			filterOptions: FILTER_PERIOD_OPTIONS,
		};
	},

	computed: {
		...mapState("reportsFilters", ["endMonth", "selectedPeriodRange"]),
		...mapState("financialReports", ["filterPeriod"]),
		...mapState("expensesReport", [
			"selectedCounterpartTypes",
			"monthHeaders",
			"isLoading",
			"error",
		]),
		...mapGetters("expensesReport", ["tableRows", "totalRow", "ratioRow", "isCuentasMode"]),

		getNow() {
			return moment().toDate();
		},

		visibleRows() {
			return this.tableRows.filter(row => {
				if (row.level === 2) return true;
				return this.expandedL2Rows.includes(row.parent);
			});
		},

		// Firma reactiva consolidada — un solo trigger para fetch aunque
		// varios filtros cambien en la misma transacción.
		fetchSignature() {
			return [
				this.endMonth,
				this.selectedPeriodRange,
				this.selectedCounterpartTypes.join(","),
				this.filterPeriod,
			].join("|");
		},

		// Las summary cards solo dependen del mes de referencia: refetch
		// independiente para no perderlas al cambiar period range o filtros.
		summarySignature() {
			return moment(this.endMonth).format("YYYY-MM");
		},
	},

	methods: {
		...mapActions("expensesReport", ["fetchExpensesData", "fetchExpensesSummary"]),
		...mapActions("reportsFilters", ["setPeriodRange"]),
		...mapMutations("reportsFilters", ["SET_END_MONTH"]),
		...mapMutations("expensesReport", [
			"SET_SELECTED_COUNTERPART_TYPES",
		]),
		...mapMutations("financialReports", ["SET_FILTER_PERIOD"]),

		updateEndMonth(date) {
			this.SET_END_MONTH(date);
		},
		updateFilterPeriod(period) {
			this.SET_FILTER_PERIOD(period);
		},
		setCuentasMode() {
			this.SET_SELECTED_COUNTERPART_TYPES([]);
		},
		toggleL2(label) {
			const index = this.expandedL2Rows.indexOf(label);
			if (index > -1) {
				this.expandedL2Rows.splice(index, 1);
			} else {
				this.expandedL2Rows.push(label);
			}
		},
	},

	mounted() {
		this.fetchExpensesData();
		this.fetchExpensesSummary();
	},

	watch: {
		fetchSignature() { this.fetchExpensesData(); },
		summarySignature() { this.fetchExpensesSummary(); },
	},
};
</script>

<style scoped lang="scss">
@import "@/assets/style.scss";

.gastos_tab {
	position: relative;
	padding-bottom: 40px;
	height: 100%;
}

.header_section_header {
	// Layout: 3 zonas en flex con wrap. Misma estrategia que VentasTab:
	// margin-left:auto en center y right hace que ambas compartan el sobrante
	// de su fila a partes iguales en una sola fila (center al midpoint, right
	// al borde derecho), y cuando una wrappea a su propia fila, su auto
	// consume todo el sobrante → queda anclada a la derecha.
	display: flex;
	flex-wrap: wrap;
	align-items: flex-end;
	gap: 16px 24px;

	// Sticky bajo .headerTop (NavigationTabs) en DashboardReports.
	// --tab-sticky-top viene del ResizeObserver de DashboardReports.
	position: sticky;
	top: var(--tab-sticky-top, 193px);
	z-index: 4;
	background: var(--report-bg);
	border-bottom: 1px solid var(--report-border);
	padding-bottom: 8px;
}

.center_sub_nav,
.right_actions {
	margin-left: auto;
}

// Viewport muy angosto: cada zona en su propia fila, stack vertical centrado.
@media (max-width: 600px) {
	.header_section_header {
		flex-direction: column;
		align-items: center;
	}

	.center_sub_nav,
	.right_actions {
		margin-left: 0;
	}

	.header_left {
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;
	}
}

.header_title {
	font-size: 32px;
	font-weight: 700;
	margin-bottom: 4px;
}

.header_description {
	font-size: 16px;
	color: var(--report-text-muted);
}

.filters_row {
	gap: 12px;
}

.sub_nav_filters {
	display: inline-flex;
	background: #ffffff;
	border: 1px solid var(--report-border);
	border-radius: 8px;
	width: fit-content;
	box-sizing: border-box;
	min-height: 34px;
}

.filter_btn {
	display: inline-flex;
	align-items: center;
	padding: 0 16px;
	min-height: 32px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	line-height: 1;
	color: #4b5563;
	cursor: pointer;
	transition: all 0.15s ease;
	box-sizing: border-box;

	&:hover { background: #f9fafb; }

	&.is-active {
		background: var(--report-primary);
		color: #ffffff;
	}
}

.calendar_picker {
	background: var(--report-primary);
	color: white;
	border-radius: 8px;
	font-weight: 500;

	::v-deep .calendar-label {
		color: white !important;
	}
}

.theme_btn_white {
	background: white;
	border: 1px solid var(--report-border);
	border-radius: 8px;
	padding: 8px 16px;
	font-weight: 500;
	color: #374151;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
	cursor: pointer;

	&:hover { background: #f9fafb; }
}
</style>
